;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();export const CAMPAIGN_ATTRIBUTION_COOKIE = 'nt_campaign';
export const CAMPAIGN_ATTRIBUTION_MAX_AGE_SECONDS = 2 * 24 * 60 * 60;

const CAMPAIGN_VALUE_MAX_LENGTH = 200;

export const CAMPAIGN_QUERY_KEYS = [
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'hsa_acc',
  'hsa_grp',
  'hsa_ad',
  'hsa_cam',
  'hsa_ver',
  'hsa_net',
] as const;

export type CampaignQueryKey = (typeof CAMPAIGN_QUERY_KEYS)[number];

export interface CampaignAttribution {
  utm_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  hsa_acc?: string;
  hsa_grp?: string;
  hsa_ad?: string;
  hsa_cam?: string;
  hsa_ver?: string;
  hsa_net?: string;
}

export interface CampaignCookieSetOptions {
  httpOnly: false;
  secure: boolean;
  sameSite: 'lax';
  path: '/';
  maxAge: number;
  domain?: string;
}

export interface CampaignCookieToSet {
  name: string;
  value: string;
  options: CampaignCookieSetOptions;
}

const CAMPAIGN_QUERY_KEY_SET = new Set<string>(CAMPAIGN_QUERY_KEYS);

function isCampaignQueryKey(key: string): key is CampaignQueryKey {
  return CAMPAIGN_QUERY_KEY_SET.has(key);
}

function normalizeCampaignValue(key: CampaignQueryKey, value: string): string {
  const trimmed = value.trim().slice(0, CAMPAIGN_VALUE_MAX_LENGTH);
  if (key === 'utm_source' || key === 'utm_medium') return trimmed.toLowerCase();
  return trimmed;
}

export function getCampaignParamsFromSearchParams(
  searchParams: URLSearchParams
): CampaignAttribution | null {
  const attribution: CampaignAttribution = {};

  for (const key of CAMPAIGN_QUERY_KEYS) {
    const rawValue = searchParams.get(key);
    if (!rawValue) continue;
    const value = normalizeCampaignValue(key, rawValue);
    if (!value) continue;
    attribution[key] = value;
  }

  return Object.keys(attribution).length > 0 ? attribution : null;
}

export function parseCampaignAttributionCookie(
  value: string | undefined | null
): CampaignAttribution | null {
  if (!value) return null;

  try {
    const decoded = decodeCookieValue(value);
    const parsed: object = JSON.parse(decoded);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const attribution: CampaignAttribution = {};

    for (const [key, rawValue] of Object.entries(parsed)) {
      if (!isCampaignQueryKey(key) || typeof rawValue !== 'string') continue;
      const normalized = normalizeCampaignValue(key, rawValue);
      if (!normalized) continue;
      attribution[key] = normalized;
    }

    return Object.keys(attribution).length > 0 ? attribution : null;
  } catch {
    return null;
  }
}

export function serializeCampaignAttribution(attribution: CampaignAttribution): string {
  return JSON.stringify(attribution);
}

function getSharedCookieDomainFromEnv(): string | undefined {
  const raw = process.env.APP_DOMAIN ?? process.env.NEXT_PUBLIC_APP_DOMAIN;
  if (!raw) return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

export function getCampaignCookieSetOptions(isHttps: boolean): CampaignCookieSetOptions {
  const domain = getSharedCookieDomainFromEnv();

  return {
    httpOnly: false,
    secure: isHttps,
    sameSite: 'lax',
    path: '/',
    maxAge: CAMPAIGN_ATTRIBUTION_MAX_AGE_SECONDS,
    ...(domain ? { domain } : {}),
  };
}

export function getFirstTouchCampaignCookieToSet({
  searchParams,
  existingCookieValue,
  isHttps,
}: {
  searchParams: URLSearchParams;
  existingCookieValue: string | undefined;
  isHttps: boolean;
}): CampaignCookieToSet | null {
  if (parseCampaignAttributionCookie(existingCookieValue)) return null;

  const attribution = getCampaignParamsFromSearchParams(searchParams);
  if (!attribution) return null;

  return {
    name: CAMPAIGN_ATTRIBUTION_COOKIE,
    value: serializeCampaignAttribution(attribution),
    options: getCampaignCookieSetOptions(isHttps),
  };
}

export function persistCampaignAttributionFromUrl(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (parseCampaignAttributionCookie(readBrowserCookie(CAMPAIGN_ATTRIBUTION_COOKIE))) return;

  const attribution = getCampaignParamsFromSearchParams(
    new URLSearchParams(window.location.search)
  );
  if (!attribution) return;

  document.cookie = [
    `${CAMPAIGN_ATTRIBUTION_COOKIE}=${encodeURIComponent(serializeCampaignAttribution(attribution))}`,
    ...getBrowserCookieAttributeParts(),
  ].join('; ');
}

export function getCampaignAttribution(): CampaignAttribution {
  if (typeof window === 'undefined') return {};

  const fromCookie = parseCampaignAttributionCookie(readBrowserCookie(CAMPAIGN_ATTRIBUTION_COOKIE));
  if (fromCookie) return fromCookie;

  return getCampaignParamsFromSearchParams(new URLSearchParams(window.location.search)) ?? {};
}

export function getCampaignTrackParams(
  campaign: CampaignAttribution = getCampaignAttribution()
): CampaignAttribution {
  const params: CampaignAttribution = {};

  for (const key of CAMPAIGN_QUERY_KEYS) {
    const value = campaign[key];
    if (value) params[key] = value;
  }

  return params;
}

export function clearCampaignAttribution(): void {
  if (typeof document === 'undefined') return;

  const expiredAttributes = [
    `${CAMPAIGN_ATTRIBUTION_COOKIE}=`,
    'Path=/',
    'Max-Age=0',
    'SameSite=Lax',
    getBrowserCookieSecureAttribute(),
  ].filter(Boolean);

  document.cookie = expiredAttributes.join('; ');

  const domain = getBrowserCookieDomainAttribute();
  if (!domain) return;

  document.cookie = [...expiredAttributes, domain].join('; ');
}

function decodeCookieValue(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function readBrowserCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const prefix = `${name}=`;

  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) return trimmed.slice(prefix.length);
  }

  return undefined;
}

function getBrowserCookieAttributeParts(): string[] {
  return [
    'Path=/',
    `Max-Age=${CAMPAIGN_ATTRIBUTION_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    getBrowserCookieDomainAttribute(),
    getBrowserCookieSecureAttribute(),
  ].filter(Boolean);
}

function getBrowserCookieDomainAttribute(): string {
  if (typeof window === 'undefined') return '';

  const domain = getSharedCookieDomainFromEnv();
  return domain ? `Domain=${domain}` : '';
}

function getBrowserCookieSecureAttribute(): string {
  if (typeof window === 'undefined' || window.location.protocol !== 'https:') return '';
  return 'Secure';
}
